/**
 * 收益账单、结算申请及相关业务状态配置与公共工具函数
 */

export interface RevenueStatusItem {
  label: string;
  color: string;
  description?: string;
}

/**
 * 2.6 数据、账单与结算统一状态映射表
 */
export const REVENUE_STATUS_MAP: Record<string, RevenueStatusItem> = {
  // 渠道数据状态 (channel_data_status)
  CHANNEL_DATA_DRAFT: { label: '数据草稿', color: 'bg-gray-100 text-gray-700 border-gray-200', description: '渠道运营数据已录入但尚未复核' },
  CHANNEL_DATA_PENDING_REVIEW: { label: '待复核数据', color: 'bg-blue-50 text-blue-700 border-blue-200', description: '等待后台复核' },
  CHANNEL_DATA_APPROVED: { label: '数据已复核', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', description: '已复核，可在前台展示' },
  CHANNEL_DATA_VOID: { label: '数据作废', color: 'bg-gray-100 text-gray-500 border-gray-200', description: '数据作废，不参与展示或后续计算' },

  // 收入周期状态 (revenue_period_status)
  REVENUE_PERIOD_DRAFT: { label: '周期草稿', color: 'bg-gray-100 text-gray-700 border-gray-200', description: '收入周期数据已录入但未完成复核' },
  REVENUE_PERIOD_PENDING_REVIEW: { label: '周期待复核', color: 'bg-blue-50 text-blue-700 border-blue-200', description: '等待财务/运营复核' },
  REVENUE_PERIOD_APPROVED: { label: '周期已确认', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', description: '收入周期确认，可生成收益账单' },
  REVENUE_PERIOD_VOID: { label: '周期作废', color: 'bg-gray-100 text-gray-500 border-gray-200', description: '收入周期作废，不可出账' },

  // 收益账单状态 (statement_status)
  PENDING_ISSUE: { label: '待生成账单', color: 'bg-slate-50 text-slate-700 border-slate-200', description: '收入周期已确认，尚未生成对客账单' },
  ISSUED: { label: '账单已生成', color: 'bg-blue-50 text-blue-700 border-blue-200', description: '账单已生成，但尚未进入内容方确认阶段' },
  PENDING_OWNER_CONFIRMATION: { label: '待内容方确认', color: 'bg-amber-50 text-amber-700 border-amber-200', description: '等待内容方确认账单' },
  OWNER_CONFIRMED: { label: '内容方已确认', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', description: '内容方已确认，等待申请结算或平台结算处理' },
  PENDING_SETTLEMENT: { label: '待结算付款', color: 'bg-indigo-50 text-indigo-700 border-indigo-200', description: '已提交有效结算申请，等待付款' },
  SETTLED: { label: '已完成结算', color: 'bg-teal-50 text-teal-700 border-teal-200', description: '对应结算已付款完成' },
  DISPUTED: { label: '账单异议中', color: 'bg-rose-50 text-rose-700 border-rose-200', description: '内容方提交异议，冻结当前账单结算' },
  VOID: { label: '账单作废', color: 'bg-gray-100 text-gray-500 border-gray-200', description: '账单作废，如收入周期作废或人工撤销' },

  // 结算申请状态 (settlement_status)
  PENDING_PROFILE: { label: '待补齐结算资料', color: 'bg-amber-50 text-amber-700 border-amber-200', description: '内容方发起结算但尚未提交或补齐结算资料' },
  PENDING_REVIEW: { label: '待审核申请', color: 'bg-blue-50 text-blue-700 border-blue-200', description: '已提交结算资料和申请，等待审核' },
  PENDING_PAYMENT: { label: '等待财务付款', color: 'bg-purple-50 text-purple-700 border-purple-200', description: '审核通过，等待财务付款' },
  PAYING: { label: '财务打款中', color: 'bg-indigo-50 text-indigo-700 border-indigo-200', description: '财务正在付款或等待银行处理结果' },
  PAID: { label: '已完成付款', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', description: '已完成付款' },
  REJECTED: { label: '已驳回', color: 'bg-rose-50 text-rose-700 border-rose-200', description: '结算资料或申请不符合要求，被驳回' },

  // 付款记录状态 (payment_status)
  PAYMENT_PENDING: { label: '待付款', color: 'bg-amber-50 text-amber-700 border-amber-200', description: '已创建付款记录，尚未实际付款' },
  PAYMENT_PAID: { label: '打款成功', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', description: '银行付款完成，已上传凭证' },
  PAYMENT_FAILED: { label: '付款失败', color: 'bg-rose-50 text-rose-700 border-rose-200', description: '付款失败，如账户错误或银行退回' },
  PAYMENT_VOID: { label: '付款作废', color: 'bg-gray-100 text-gray-500 border-gray-200', description: '付款记录作废' },

  // 兼容简化状态与审核结果 (result)
  DRAFT: { label: '草稿', color: 'bg-gray-100 text-gray-700 border-gray-200', description: '草稿状态' },
  PENDING: { label: '待处理', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  PROCESSING: { label: '处理中', color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  PAYMENT_PROCESSING: { label: '财务打款中', color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  CONFIRMED: { label: '已确认', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  COMPLETED: { label: '已完成', color: 'bg-teal-50 text-teal-700 border-teal-200' },
  APPROVED: { label: '审核通过', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  PASS: { label: '审核通过', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  PASSED: { label: '审核通过', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  ACCEPT: { label: '审核通过', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  ACCEPTED: { label: '审核通过', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  REJECT: { label: '已驳回', color: 'bg-rose-50 text-rose-700 border-rose-200' },
  REFUSED: { label: '已拒绝', color: 'bg-rose-50 text-rose-700 border-rose-200' },
  DENIED: { label: '已拒绝', color: 'bg-rose-50 text-rose-700 border-rose-200' },
  FAILED: { label: '审核未通过', color: 'bg-rose-50 text-rose-700 border-rose-200' },
  FAIL: { label: '审核未通过', color: 'bg-rose-50 text-rose-700 border-rose-200' },
  REVIEWING: { label: '审核中', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  UNDER_REVIEW: { label: '审核中', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  RESUBMIT: { label: '需重新提交', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  NEED_MODIFY: { label: '需修改资料', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  SETTLEMENT_PROFILE: { label: '结算资料审核', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  SETTLEMENT_APPLY: { label: '结算申请审核', color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  SETTLEMENT_REQUEST: { label: '结算申请', color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  SETTLEMENT: { label: '结算单', color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  STATEMENT: { label: '收益账单', color: 'bg-purple-50 text-purple-700 border-purple-200' },
  REVENUE_STATEMENT: { label: '收益账单', color: 'bg-purple-50 text-purple-700 border-purple-200' },
  DISPUTE: { label: '账单异议', color: 'bg-rose-50 text-rose-700 border-rose-200' },
};

/**
 * 根据状态标识获取统一的状态文案与 Badge 样式
 * @param status 状态字符串
 */
export function getRevenueStatusInfo(status?: string): RevenueStatusItem {
  if (!status) {
    return { label: '未知', color: 'bg-gray-50 text-gray-600 border-gray-200' };
  }
  const upperKey = status.toUpperCase();
  return (
    REVENUE_STATUS_MAP[upperKey] ||
    REVENUE_STATUS_MAP[status] || { label: status, color: 'bg-gray-50 text-gray-600 border-gray-200' }
  );
}

/**
 * 判断指定账单/结算单状态是否可以由内容方点击确认
 * @param status 账单或结算单状态标识
 */
export function canConfirmBill(status?: string): boolean {
  if (!status) return false;
  const s = status.toUpperCase();
  return (
    s === 'PENDING_OWNER_CONFIRMATION' ||
    s === 'PENDING_CONFIRMATION' ||
    s === 'PENDING' ||
    s === 'ISSUED'
  );
}

/**
 * 判断内容方已确认的账单是否可点击“申请结算”
 * @param status 账单状态标识
 */
export function canApplySettlement(status?: string): boolean {
  if (!status) return false;
  const s = status.toUpperCase();
  return s === 'OWNER_CONFIRMED' || s === 'CONFIRMED';
}

/**
 * 根据状态获取统一的图标与背景样式
 * @param status 状态标识
 */
export function getRevenueStatusIcon(status?: string): { icon: string; bg: string } {
  if (!status) {
    return { icon: 'ri-question-line text-gray-500', bg: 'bg-gray-100' };
  }
  const s = status.toUpperCase();

  // 已确认 / 已完成 / 已打款
  if (
    s === 'OWNER_CONFIRMED' ||
    s === 'CONFIRMED' ||
    s === 'SETTLED' ||
    s === 'PAID' ||
    s === 'APPROVED' ||
    s === 'PASSED' ||
    s === 'PASS' ||
    s === 'ACCEPTED' ||
    s === 'ACCEPT' ||
    s === 'COMPLETED' ||
    s === 'PAYMENT_PAID' ||
    s === 'CHANNEL_DATA_APPROVED' ||
    s === 'REVENUE_PERIOD_APPROVED'
  ) {
    return { icon: 'ri-check-line text-emerald-600', bg: 'bg-emerald-100' };
  }

  // 异议 / 驳回 / 失败 / 作废
  if (
    s === 'DISPUTED' ||
    s === 'DISPUTE' ||
    s === 'REJECTED' ||
    s === 'REJECT' ||
    s === 'REFUSED' ||
    s === 'DENIED' ||
    s === 'FAILED' ||
    s === 'FAIL' ||
    s === 'PAYMENT_FAILED' ||
    s.endsWith('_VOID') ||
    s === 'VOID'
  ) {
    return { icon: 'ri-error-warning-line text-rose-600', bg: 'bg-rose-100' };
  }

  // 打款中 / 处理中 / 审核中
  if (
    s === 'PAYING' ||
    s === 'PAYMENT_PROCESSING' ||
    s === 'PROCESSING' ||
    s === 'REVIEWING' ||
    s === 'UNDER_REVIEW' ||
    s === 'PENDING_REVIEW'
  ) {
    return { icon: 'ri-loader-4-line text-indigo-600', bg: 'bg-indigo-100' };
  }

  // 待确认 / 待结算 / 待付款 / 默认 pending 类
  return { icon: 'ri-time-line text-amber-600', bg: 'bg-amber-100' };
}

